/**
 * Number and time formatting.
 *
 * Every figure on every screen goes through here so the conventions the design fixes are
 * not re-decided per component: explicit currency, explicit sign on P&L, decimal odds,
 * tabular alignment (via `.num` / `.u-mono` in the CSS).
 */

import type { Currency } from '../../shared/types.ts'

const GBP = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const SYMBOL: Record<Currency, string> = { GBP: '£', USD: '$', EUR: '€' }

/**
 * The player's own money — stakes, P&L, their bankroll — is in the currency of their
 * current round. The shell sets it once the context arrives; until then it reads £.
 */
let own: Currency = 'GBP'
export function setCurrency(c: Currency) {
  own = c
}

/** 10,000.00 in a given currency, for someone else's figures: $1,234.56. */
export function moneyIn(n: number | null | undefined, currency: Currency): string {
  if (n === null || n === undefined) return '—'
  return `${n < 0 ? '−' : ''}${SYMBOL[currency]}${GBP.format(Math.abs(n))}`
}

/** £1,234.56 — no sign, in the player's currency. Use for liability, stake, thresholds. */
export function gbp(n: number | null | undefined): string {
  return moneyIn(n, own)
}

/**
 * £1.84m / £920k / £412 — for exchange-wide matched volume, which runs to millions and is
 * read as a magnitude rather than an amount. Never use it for our own money: a stake or a
 * liability rounded to two significant figures is a stake we have got wrong.
 */
export function gbpCompact(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  const abs = Math.abs(n)
  const sign = n < 0 ? '−' : ''
  if (abs >= 1_000_000) return `${sign}£${(abs / 1_000_000).toFixed(2)}m`
  if (abs >= 1_000) return `${sign}£${Math.round(abs / 1_000)}k`
  return `${sign}£${Math.round(abs)}`
}

/** +£184.20 / −£12.30 — always signed, in the player's currency. Use for P&L, never for liability. */
export function pnl(n: number | null | undefined, currency: Currency = own): string {
  if (n === null || n === undefined) return '—'
  if (Math.abs(n) < 0.005) return `${SYMBOL[currency]}${GBP.format(0)}`
  return `${n > 0 ? '+' : '−'}${SYMBOL[currency]}${GBP.format(Math.abs(n))}`
}

/** The class that colours a P&L figure. Always paired with the sign in the text. */
export function pnlClass(n: number | null | undefined): string {
  if (n === null || n === undefined || Math.abs(n) < 0.005) return 'muted'
  return n > 0 ? 'pos' : 'neg'
}

export function odds(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : n.toFixed(2)
}

export function count(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : n.toLocaleString('en-GB')
}

export function pct(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : `${Math.round(n)}%`
}

/** Time of day in the user's timezone, to the second — decisions are sequenced by it. */
export function clock(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleTimeString('en-GB', { hour12: false })
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** "41s ago", "12m ago". Never rounds up to zero — staleness must stay visible. */
export function ago(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return 'never'
  if (seconds < 60) return `${Math.round(seconds)}s ago`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`
  return `${Math.round(seconds / 86400)}d ago`
}

export function duration(seconds: number): string {
  const s = Math.abs(seconds)
  if (s < 60) return `${Math.round(s)}s`
  if (s < 3600) return `${Math.floor(s / 60)}m`
  const h = Math.floor(s / 3600)
  const m = Math.round((s % 3600) / 60)
  return m ? `${h}h ${m}m` : `${h}h`
}

/**
 * The countdown to kick-off, a primary attribute of every market object.
 * Past kick-off it reports how long ago the market started, never a negative number.
 */
export function toKickOff(startIso: string | null | undefined): { label: string; started: boolean } {
  if (!startIso) return { label: 'No kick-off time', started: false }
  const deltaS = (new Date(startIso).getTime() - Date.now()) / 1000
  if (deltaS <= 0) return { label: `started ${duration(-deltaS)} ago`, started: true }
  return { label: `${duration(deltaS)} to kick-off`, started: false }
}

/** Market-local kick-off time, shown alongside the countdown. */
export function kickOffTime(startIso: string | null | undefined): string {
  if (!startIso) return '—'
  return new Date(startIso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
}
